import { annotateFacetExclusions, getUserExcludedFacets } from './searchExclusions';
import { getAuthToken, getZipCodeGeoCoordinates } from './user';
import { getPromotedSearchResults, searchPromoted } from './searchPromoted';
import { getRawArchivedSearchFacets, getRawUpcomingSearchFacets } from './searchFacets';
import {
    LOAD_CATEGORY_RESULTS_FAIL,
    LOAD_CATEGORY_RESULTS_REQUEST,
    LOAD_CATEGORY_RESULTS_SUCCESS,
    LOAD_DISCOVER_ITEMS_SUCCESS,
    LOAD_MORE_SEARCH_RESULTS_FAIL,
    LOAD_MORE_SEARCH_RESULTS_REQUEST,
    LOAD_MORE_SEARCH_RESULTS_SUCCESS,
    LOAD_SEARCH_RESULTS_FAIL,
    LOAD_SEARCH_RESULTS_REQUEST,
    LOAD_SEARCH_RESULTS_SUCCESS,
    SET_USE_GEO_LOCATION,
} from './actions';
import api from '@/redux/api/search';
import { blender } from '@/utils/blender';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import facetTypes from '@/utils/search/facetTypes';
import { getAllItems } from './item';
import { getDeployment } from './config';
// import { getItemCardDatas } from './cardData';getSearchedItems
import { GlobalState } from '../rootReducer';

const UPDATE_SEARCH_INPUT_BUFFER = 'UPDATE_SEARCH_INPUT_BUFFER';
const UPDATE_SEARCH_INPUT_BUFFER_FAIL = 'UPDATE_SEARCH_INPUT_BUFFER_FAIL';

/* reducer */
const DEFAULT_STATE = {
    error: false,
    facets: [],
    isLoading: true,
    itemIds: [],
    searchInputBuffer: '',
    soldItemFacets: [],
    sort: [],
    submitted: false,
    success: false,
    totalFound: 0,
    useGeoLocation: false,
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case LOAD_SEARCH_RESULTS_REQUEST:
        case LOAD_CATEGORY_RESULTS_REQUEST:
        case LOAD_MORE_SEARCH_RESULTS_REQUEST:
            return {
                ...state,
                error: false,
                submitted: true,
                success: false,
            };
        case LOAD_SEARCH_RESULTS_SUCCESS:
        case LOAD_CATEGORY_RESULTS_SUCCESS:
        case LOAD_DISCOVER_ITEMS_SUCCESS:
            return {
                ...state,
                facets: action.payload.facets,
                isLoading: false,
                itemIds: action.payload.itemIDs,
                soldItemFacets: action.payload.soldItemFacets,
                soldItemIds: action.payload.soldItemIDs,
                sort: action.payload.sort.filter((sortOption) => sortOption !== 'publishDate'),
                submitted: false,
                success: true,
                totalBuyNowItems: action.payload.totalBuyNow,
                totalFound: action.payload.totalFound,
                totalLiveItems: action.payload.totalLive,
                totalPages: action.payload.totalPages,
                totalSoldItems: action.payload.totalSold,
            };
        case LOAD_MORE_SEARCH_RESULTS_SUCCESS:
            const ids = cloneDeep(state.itemIds);
            action.payload.itemIDs.forEach((i) => ids.push(i));
            return {
                ...state,
                facets: action.payload.facets,
                isLoading: false,
                itemIds: ids,
                soldItemFacets: action.payload.soldItemFacets,
                soldItemIds: action.payload.soldItemIDs,
                sort: action.payload.sort.filter((sortOption) => sortOption !== 'publishDate'),
                submitted: false,
                success: true,
                totalBuyNowItems: action.payload.totalBuyNow,
                totalFound: action.payload.totalFound,
                totalLiveItems: action.payload.totalLive,
                totalPages: action.payload.totalPages,
                totalSoldItems: action.payload.totalSold,
            };
        case LOAD_MORE_SEARCH_RESULTS_FAIL:
        case LOAD_SEARCH_RESULTS_FAIL:
        case LOAD_CATEGORY_RESULTS_FAIL:
        case UPDATE_SEARCH_INPUT_BUFFER_FAIL:
            return {
                ...state,
                error: true,
                submitted: false,
                success: false,
            };
        case SET_USE_GEO_LOCATION:
            return {
                ...state,
                useGeoLocation: action.payload,
            };
        case UPDATE_SEARCH_INPUT_BUFFER:
            return {
                ...state,
                searchInputBuffer: action.payload,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.search;
const idSelector = (state: GlobalState, id) => id;
const idSecondSelector = (state: GlobalState, _, id) => id;

export const getUseGeoLocation = createSelector(stateSelector, (state) => state.useGeoLocation);

export const getSearchInputBuffer = createSelector(stateSelector, (state) => state.searchInputBuffer);

export const getSearchedItemIds = createSelector(stateSelector, (state) => state.itemIds || []);

const getSearchedItems = createSelector([getSearchedItemIds, getAllItems], (itemIds, allItems) => {
    return itemIds.map((itemId) => allItems[itemId]).filter((i) => i); // ensure that we only send back items that exist
});

export const getFirstSearchItem = createSelector(getSearchedItems, (items) => items?.[0] || {});

export const getSellersFromSearchedItems = createSelector(getSearchedItems, (items) =>
    items.map((item) => item.sellerId)
);

export const getSearchedAndPromotedItems = createSelector(
    [idSelector, getSearchedItemIds, getPromotedSearchResults],
    (columns, itemIds, promotedItemIds) => {
        return blender(itemIds, promotedItemIds, columns);
    }
);

export const getSearchUiState = createSelector(stateSelector, (state) => {
    return {
        error: state.error,
        isLoading: state.isLoading,
        // @ts-ignore
        soldItemIds: state.soldItemIds || [],
        sort: state.sort,
        submitted: state.submitted,
        // @ts-ignore
        totalBuyNowItems: state.totalBuyNowItems,
        totalFound: state.totalFound,
        // @ts-ignore
        totalLiveItems: state.totalLiveItems,
        // @ts-ignore
        totalPages: state.totalPages,
        // @ts-ignore
        totalSoldItems: state.totalSoldItems,
    };
});

// For each key in the exclusions object, find the raw facet with that key, and add
// it to the search facets array
export const getArchivedSearchFacets: (state: GlobalState) => any = createSelector(
    [stateSelector, getUserExcludedFacets, getRawArchivedSearchFacets],
    (search, facetExclusions, rawFacets) => {
        const facets = cloneDeep(search.facets) || [];

        // for each exclusion, find the corresponding raw facet option, and add it
        Object.keys(facetExclusions).forEach((complexFacetId) => {
            const index = facets.findIndex((f) => f.id === complexFacetId);
            const complexFacet = rawFacets.find((f) => f.id === complexFacetId);
            if (index === -1 || !complexFacet || !complexFacet.options) {
                return;
            }
            // add excluded options to the options present in the search results
            facets[index].options = facetExclusions[complexFacetId].reduce((options, exclusion) => {
                const includedOptionsIndex = options.findIndex((o) => o.id === exclusion);
                const rawFacetOption = complexFacet.options.find((o) => o.id === exclusion);
                if (!rawFacetOption) {
                    return options;
                }
                if (includedOptionsIndex === -1) {
                    return [rawFacetOption, ...options];
                }
                return options.splice(includedOptionsIndex, 1, rawFacetOption);
            }, facets[index].options || []);
        });
        return annotateFacetExclusions(facets, facetExclusions);
    }
);

export const getUpcomingSearchFacets: (state: GlobalState) => any = createSelector(
    [stateSelector, getUserExcludedFacets, getRawUpcomingSearchFacets],
    (search, facetExclusions, rawFacets) => {
        const facets = cloneDeep(search.facets) || [];

        // for each exclusion, find the corresponding raw facet option, and add it
        Object.keys(facetExclusions).forEach((complexFacetId) => {
            const index = facets.findIndex((f) => f.id === complexFacetId);
            const complexFacet = rawFacets.find((f) => f.id === complexFacetId);
            if (index === -1 || !complexFacet || !complexFacet.options) {
                return;
            }
            // add excluded options to the options present in the search results
            facets[index].options = facetExclusions[complexFacetId].reduce((options, exclusion) => {
                const includedOptionsIndex = options.findIndex((o) => o.id === exclusion);
                const rawFacetOption = complexFacet.options.find((o) => o.id === exclusion);
                if (!rawFacetOption) {
                    return options;
                }
                if (includedOptionsIndex === -1) {
                    return [rawFacetOption, ...options];
                }
                return options.splice(includedOptionsIndex, 1, rawFacetOption);
            }, facets[index].options || []);
        });
        return annotateFacetExclusions(facets, facetExclusions);
    }
);

const getUpcomingRecommendedSearchFacets: (state: GlobalState) => any = createSelector(
    [stateSelector, getUserExcludedFacets, getRawUpcomingSearchFacets],
    (search, facetExclusions, rawFacets) => {
        const facets = cloneDeep(search.soldItemFacets) || [];

        // for each exclusion, find the corresponding raw facet option, and add it
        Object.keys(facetExclusions).forEach((complexFacetId) => {
            const index = facets.findIndex((f) => f.id === complexFacetId);
            const complexFacet = rawFacets.find((f) => f.id === complexFacetId);
            if (index === -1 || !complexFacet || !complexFacet.options) {
                return;
            }
            // add excluded options to the options present in the search results
            facets[index].options = facetExclusions[complexFacetId].reduce((options, exclusion) => {
                const includedOptionsIndex = options.findIndex((o) => o.id === exclusion);
                const rawFacetOption = complexFacet.options.find((o) => o.id === exclusion);
                if (!rawFacetOption) {
                    return options;
                }
                if (includedOptionsIndex === -1) {
                    return [rawFacetOption, ...options];
                }
                return options.splice(includedOptionsIndex, 1, rawFacetOption);
            }, facets[index].options || []);
        });
        return annotateFacetExclusions(facets, facetExclusions);
    }
);

export const getSelectedOptionFacetValues: (
    state: GlobalState,
    archivedSearch: boolean,
    id: any
) => any = createSelector(
    [idSelector, idSecondSelector, getUpcomingSearchFacets, getArchivedSearchFacets],
    (archivedSearch, id, upcomingSearchFacets, archivedSearchFacets) => {
        const facets = archivedSearch ? archivedSearchFacets : upcomingSearchFacets;
        const facetValues = facets.find((f) => f.id === id);
        const selectedFacetsValues =
            facetValues && facetValues.options ? facetValues.options.find((f) => f.selected) : {};
        return selectedFacetsValues || {};
    }
);

export const getSoldFacetsSelectedCreator: (state: GlobalState) => any = createSelector(
    getUpcomingRecommendedSearchFacets,
    (recommendedFacets) => {
        const facet = recommendedFacets?.find((x) => x.id === 'creator');
        if (!facet) {
            return [];
        }
        const { options: creators } = facet;
        return creators.find((c) => c.selected);
    }
);

export const getSoldItemImportantCreators: (state: GlobalState) => any = createSelector(
    [stateSelector, getUpcomingRecommendedSearchFacets],
    (search, recommendedFacets) => {
        const facet = recommendedFacets?.find((x) => x.id === 'creator');
        if (!facet) {
            return [];
        }
        // @ts-ignore
        const { totalSoldItems = 0 } = search;
        const { options: creators } = facet;
        return cloneDeep(creators.filter((creator) => creator.count / totalSoldItems >= 0.1));
    }
);

export const getSoldItemImportantStylePeriod: (state: GlobalState) => any = createSelector(
    [stateSelector, getUpcomingRecommendedSearchFacets],
    (search, recommendedFacets) => {
        const facet = recommendedFacets?.find((x) => x.id === 'stylePeriod');
        if (!facet) {
            return [];
        }
        // @ts-ignore
        const { totalSoldItems = 0 } = search;
        const { options: creators } = facet;
        return cloneDeep(creators.filter((creator) => creator.count / totalSoldItems >= 0.1));
    }
);

// F1 indicates category facet
export const getSoldItemImportantCategories: (state: GlobalState) => any = createSelector(
    [stateSelector, getUpcomingRecommendedSearchFacets],
    (search, recommendedFacets) => {
        const facet = recommendedFacets?.find((x) => x.id === 'F1');
        if (!facet) {
            return [];
        }
        const { categories: facets } = facet;
        // @ts-ignore
        const { totalSoldItems = 0 } = search;

        let categories = [];
        getAllCategories(categories, facets, 0);
        return categories
            .filter((category) => category.count / totalSoldItems >= 0.1)
            .sort((a, b) => b.count - a.count)
            .sort((a, b) => b.level - a.level);
    }
);

// F1 indicates category facet
export const getItemImportantCategories: (state: GlobalState) => any = createSelector(
    getUpcomingSearchFacets,
    (searchFacets) => {
        const categoryFacet = searchFacets?.find((x) => x.id === 'F1');
        if (!categoryFacet) {
            return [];
        }

        const {
            categories: { facets },
        } = categoryFacet;

        let categories = [];
        getAllCategories(categories, facets, 0);
        return categories.sort((a, b) => b.count - a.count).sort((a, b) => b.level - a.level);
    }
);

// F2 indicates origin facet
export const getSoldItemImportantOrigins: (state: GlobalState) => any = createSelector(
    [stateSelector, getUpcomingRecommendedSearchFacets],
    (search, recommendedFacets) => {
        const facet = recommendedFacets?.find((x) => x.id === 'F2');
        if (!facet) {
            return [];
        }
        const { categories: facets } = facet;
        // @ts-ignore
        const { totalSoldItems = 0 } = search;

        let categories = [];
        getAllCategories(categories, facets, 0);
        return categories
            .filter((category) => category.count / totalSoldItems >= 0.1)
            .sort((a, b) => b.count - a.count)
            .sort((a, b) => b.level - a.level);
    }
);

const getAllCategories = (returnCategories: any[], facets: any[], level: number) => {
    level++;
    facets.forEach(({ categories, count, id, name }) => {
        returnCategories.push({ count, id, level, name });
        if (categories) {
            getAllCategories(returnCategories, categories, level);
        }
    });
};

export const getUpcomingAuctions: (state: GlobalState, archivedSearch: boolean) => any = createSelector(
    [idSelector, getUpcomingSearchFacets, getArchivedSearchFacets],
    (archivedSearch, upcomingSearchFacets, archivedSearchFacets) => {
        const facets = archivedSearch ? archivedSearchFacets : upcomingSearchFacets;
        const facetValues = facets.find((f) => f.id === facetTypes.AUCTION_HOUSE_ID);
        return facetValues ? facetValues.options.length : 0;
    }
);

export const getCategoryWithGreatestResults: (state: GlobalState, archivedSearch: boolean) => any = createSelector(
    [idSelector, getUpcomingSearchFacets, getArchivedSearchFacets],
    (archivedSearch, upcomingSearchFacets, archivedSearchFacets) => {
        const facets = archivedSearch ? archivedSearchFacets : upcomingSearchFacets;
        const categoryFacets = facets.find((f) => f.id === facetTypes.CATEGORY_ID);
        const category =
            categoryFacets && categoryFacets.categories && categoryFacets.categories.facets.length > 0
                ? categoryFacets.categories.facets[0]
                : {};
        return category;
    }
);

export const getAugmentedSearchQuery = (searchQuery, searchExclusions) => {
    const augmentedOptions = !searchExclusions
        ? searchQuery.options
        : Object.keys(searchExclusions).reduce(
              (options, facetWithExclusions) => ({
                  ...options,
                  [facetWithExclusions]: [
                      {
                          exclude: searchExclusions[facetWithExclusions] || [],
                          include: searchQuery.options[facetWithExclusions] || [],
                      },
                  ],
              }),
              { ...searchQuery.options }
          );
    const augmentedSearchQuery = {
        ...searchQuery,
        options: augmentedOptions,
    };

    if (augmentedSearchQuery?.options?.auctionHouse) {
        augmentedSearchQuery?.options?.auctionHouse[0]?.exclude?.slice(0, 200);
    }

    if (augmentedSearchQuery?.options?.topRated) {
        augmentedSearchQuery.topRated = true;
    }

    return augmentedSearchQuery;
};

/* ACTION CREATORS */
export const submitSearch = (searchQuery: any, pageName: string) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const deployment = getDeployment(state);
        dispatch({
            payload: searchQuery,
            type: LOAD_SEARCH_RESULTS_REQUEST,
        });

        const authToken = getAuthToken(state);

        // Annotate facets which have user-defined search exclusions
        const searchExclusions = getUserExcludedFacets(state);
        const augmentedSearchQuery = getAugmentedSearchQuery(searchQuery, searchExclusions);

        const useGeoLocation = getUseGeoLocation(state);
        const geoCoordinates = getZipCodeGeoCoordinates(state);
        const promises = [
            api.fetchSearchResults({
                authToken,
                deployment,
                geoCoordinates,
                searchQuery: augmentedSearchQuery,
                useGeoLocation,
            }),
        ];

        if (pageName !== 'catalog') {
            promises.push(dispatch(searchPromoted({ pageName, searchQuery })));
        }

        const [results] = await Promise.all(promises);
        if (results.error) {
            dispatch({
                error: true,
                meta: { searchQuery },
                payload: results,
                type: LOAD_SEARCH_RESULTS_FAIL,
            });
        } else {
            dispatch({
                payload: results.payload,
                type: LOAD_SEARCH_RESULTS_SUCCESS,
            });
        }
    } catch (error) {
        dispatch({
            error: true,
            meta: { searchQuery },
            payload: error,
            type: LOAD_SEARCH_RESULTS_FAIL,
        });
    }
};

export const updateSearchInputBuffer = (keyword: string) => async (dispatch: Function) => {
    try {
        if (Boolean(keyword)) {
            dispatch({
                payload: keyword,
                type: UPDATE_SEARCH_INPUT_BUFFER,
            });
        }
    } catch (error) {
        dispatch({
            error: true,
            meta: { keyword },
            payload: error,
            type: UPDATE_SEARCH_INPUT_BUFFER_FAIL,
        });
    }
};

export const fetchMoreSearchResults = (searchQuery: any, pageName: string) => async (
    dispatch: Function,
    getState: Function
) => {
    try {
        const state = getState();
        const deployment = getDeployment(state);
        dispatch({
            payload: searchQuery,
            type: LOAD_MORE_SEARCH_RESULTS_REQUEST,
        });

        const authToken = getAuthToken(state);

        // Annotate facets which have user-defined search exclusions
        const searchExclusions = getUserExcludedFacets(state);
        const augmentedSearchQuery = getAugmentedSearchQuery(searchQuery, searchExclusions);

        const useGeoLocation = getUseGeoLocation(state);
        const geoCoordinates = getZipCodeGeoCoordinates(state);

        const promises = [
            api.fetchSearchResults({
                authToken,
                deployment,
                geoCoordinates,
                searchQuery: augmentedSearchQuery,
                useGeoLocation,
            }),
        ];

        if (pageName !== 'catalog') {
            promises.push(dispatch(searchPromoted({ pageName, searchQuery })));
        }

        const [results] = await Promise.all(promises);
        if (results.error) {
            dispatch({
                error: true,
                meta: { searchQuery },
                payload: results,
                type: LOAD_SEARCH_RESULTS_FAIL,
            });
        } else {
            dispatch({
                payload: results.payload,
                type: LOAD_MORE_SEARCH_RESULTS_SUCCESS,
            });
        }
    } catch (error) {
        dispatch({
            error: true,
            meta: { searchQuery },
            payload: error,
            type: LOAD_MORE_SEARCH_RESULTS_FAIL,
        });
    }
};

export const setUseGeoLocation = (useGeoLocation: Boolean) => ({
    payload: useGeoLocation,
    type: SET_USE_GEO_LOCATION,
});
